import Modal from '@components/Modal';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { isPhoneValid } from '@utils/validate';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { TagsInput } from 'react-tag-input-component';
import { fetchClients, sendRegSMS, updateUser } from '../redux/user';
import styles from './addPatient/AddPatient.module.css';
import { useTranslation } from 'react-i18next';

const timeDiffOptions = [
  -13, -12.5, -12, -11.5, -11, -10.5, -10, -9.5, -9, -8.5, -8, -7.5, -7, -6.5,
  -6, -5.5, -5, -4.5, -4, -3.5, -3, -2.5, -2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2,
  2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11,
  11.5, 12, 12.5, 13,
];

function Patientinformation() {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    setFocus,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const dispatch = useDispatch();
  const { search } = useLocation();
  const { clientID } = useParams();
  const patient = useSelector(
    ({ user: { clients } }) =>
      (clients || []).filter(({ id }) => `${id}` === clientID)[0]
  );

  const [isOpenReactiveNotice, setOpenReactiveNotice] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [t] = useTranslation();

  const isReactive = React.useMemo(() => {
    const query = new URLSearchParams(search);
    return query.get('reactive');
  }, [search]);

  useEffect(() => {
    if (!patient) {
      return;
    }

    reset({
      firstname: patient.firstname,
      lastname: patient.lastname,
      msisdn: patient.msisdn,
      usercode: patient.usercode,
      clientid: patient.clientID,
      smslevel: patient.smsLevel,
      threshold: patient.ppmLimit,
      smsrecipients: patient.smsRecipients
        ? patient.smsRecipients.split(',')
        : [],
      timeDiff: patient.timeDiff || 0,
      ppmVisible: patient.ppmVisible || false,
    });

    if (!patient.msisdn && isReactive) {
      setOpenReactiveNotice(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, isReactive]);

  const onSubmit = async (data) => {
    const userData = {
      ...data,
      userID: parseInt(clientID, 10),
    };

    clearErrors('msisdn');
    setSaveError('');
    setSaveSuccess('');

    try {
      const result = await dispatch(updateUser(userData));

      if (result?.type?.endsWith('_FAIL')) {
        const status = result?.error?.response?.status || result?.error?.status;

        if (status === 409) {
          setError('msisdn', {
            type: 'server',
            message: 'Klientens telefonnummer finns redan.',
          });
          setFocus('msisdn');
          return;
        }

        setSaveError('Det gick inte att spara ändringarna. Försök igen.');
        console.log(result.error);
        return;
      }

      await dispatch(fetchClients());
      setSaveSuccess('Ändringarna har sparats.');
    } catch (err) {
      if (err?.response?.status === 409) {
        setError('msisdn', {
          type: 'server',
          message: 'Klientens telefonnummer finns redan.',
        });
        setFocus('msisdn');
        return;
      }

      setSaveError('Det gick inte att spara ändringarna. Försök igen.');
      console.log(err);
    }
  };

  const sendSMS = () => {
    console.log('sendSMS ' + clientID);
    dispatch(
      sendRegSMS({ userID: parseInt(clientID), message: 'Test sms message' })
    )
      .then(alert('Ett aktiverings-SMS har skickats till klienten'))
      .catch(console.log);
  };

  if (!patient) return null;

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <h2>Klientinställningar</h2>
        <p>
          {patient.firstname} {patient.lastname} (
          {patient.usercode.toUpperCase()})
        </p>
      </div>
      <div className={styles.profile}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.form}>
            <div className={styles.firstName}>
              <label>FÖRNAMN *</label>
              <input
                type="text"
                {...register('firstname', {
                  required: true,
                  maxLength: 20,
                  pattern: /^[A-Za-zäöåÄÖÅ\s-]+$/i,
                })}
                placeholder="Förnamn"
              />
              {errors.firstname && errors.firstname.type === 'required' && (
                <span className="error-helper">
                  Du måste fylla i ett förnamn
                </span>
              )}
              {errors.firstname && errors.firstname.type === 'maxLength' && (
                <span className="error-helper">Förnamnet är för långt!</span>
              )}
              {errors.firstname && errors.firstname.type === 'pattern' && (
                <span className="error-helper">
                  Ogiltiga tecken – använd bara bokstäver, mellanslag eller
                  bindestreck
                </span>
              )}
            </div>
            <div className={styles.lastName}>
              <label>EFTERNAMN *</label>
              <input
                type="text"
                {...register('lastname', {
                  required: true,
                  maxLength: 40,
                  pattern: /^[A-Za-zäöåÄÖÅ\s-]+$/i,
                })}
                placeholder="Efternamn"
              ></input>
              {errors.lastname && errors.lastname.type === 'required' && (
                <span className="error-helper">
                  Du måste fylla i ett efternamn
                </span>
              )}
              {errors.lastname && errors.lastname.type === 'maxLength' && (
                <span className="error-helper">Efternamnet är för långt!</span>
              )}
              {errors.lastname && errors.lastname.type === 'pattern' && (
                <span className="error-helper">
                  Ogiltiga tecken – använd bara bokstäver, mellanslag eller
                  bindestreck
                </span>
              )}
            </div>
            <div className={styles.phonenumber}>
              <label>KLIENTENS TELEFONNUMMER *</label>
              <input
                type="text"
                {...register('msisdn', {
                  required: true,
                  maxLength: 10,
                  minLength: 8,
                  pattern: /[0-9]{10}/,
                })}
                placeholder="Telefonnummer"
              ></input>
              {errors.msisdn && errors.msisdn.type === 'required' && (
                <span className="error-helper">
                  Du måste fylla i ett telefonnummer
                </span>
              )}
              {errors.msisdn && errors.msisdn.type === 'maxLength' && (
                <span className="error-helper">
                  Telefonnummret har för många sifror. max 10
                </span>
              )}
              {errors.msisdn && errors.msisdn.type === 'minLength' && (
                <span className="error-helper">
                  Telfonnummret innehåller för få siffror, min 8
                </span>
              )}
              {errors.msisdn && errors.msisdn.type === 'pattern' && (
                <span className="error-helper">Endast siffror</span>
              )}
              {errors.msisdn && errors.msisdn.type === 'server' && (
                <span className="error-helper">{errors.msisdn.message}</span>
              )}
            </div>
            <div className={styles.patientId}>
              <div className={styles.flex}>
                <label>ALBUS-ID</label>
                <Tooltip title="ID:t genereras slumpmässigt för att skydda din klientens integritet.">
                  <InfoOutlinedIcon style={{ color: 'blue' }} />
                </Tooltip>
              </div>
              <input
                type="text"
                {...register('usercode')}
                placeholder="MANI"
                readOnly
              ></input>
            </div>
            <div className={styles.altpatientId}>
              <label>EGET ID</label>
              <input
                type="text"
                {...register('clientid', { maxLength: 12 })}
                placeholder="Eget ID"
              ></input>
              {errors.clientid && errors.clientid.type === 'maxLength' && (
                <span className="error-helper">ID:et är för långt. max 12</span>
              )}
            </div>
            <div
              className={classNames(styles.contactPhonenumber, {
                error: errors?.smsrecipients,
              })}
            >
              <div className={styles.flex}>
                <label>{t('phoneNumberForResultSharing')}</label>
                <Tooltip title={t('inputMultiplePhoneNumberTooltip')}>
                  <InfoOutlinedIcon style={{ color: 'blue' }} />
                </Tooltip>
              </div>
              <Controller
                name="smsrecipients"
                control={control}
                render={({
                  field: { ref, onChange, onBlur, value, ...restField },
                }) => (
                  <TagsInput
                    placeHolder={t('inputMultiplePhoneNumberPlaceholder')}
                    beforeAddValidate={(phone) => {
                      const isValid = isPhoneValid(phone);
                      if (!isValid) {
                        console.log('set error');
                        setError('smsrecipients', { type: 'isPhoneNumber' });
                      }
                      return isValid;
                    }}
                    value={value}
                    onChange={(x) => onChange(x)}
                    onBlur={(e) => {
                      const currentInputValue = e.target.value;
                      clearErrors('smsrecipients');
                      if (!currentInputValue) return;
                      if (isPhoneValid(currentInputValue)) {
                        if (
                          !value?.includes(currentInputValue) &&
                          currentInputValue !== ''
                        ) {
                          onChange([...(value || []), currentInputValue]);
                          e.target.value = '';
                        }
                      } else {
                        setError('smsrecipients', { type: 'isPhoneNumber' });
                      }
                    }}
                    {...restField}
                  />
                )}
              />
              {errors?.smsrecipients?.type === 'isPhoneNumber' && (
                <span className="error-helper">
                  Telefonnumret är inte i rätt format
                </span>
              )}
            </div>
            <div className={styles.signal}>
              <div className={styles.flex}>
                <label>DELA RESULTAT VIA SMS VID:</label>
                <Tooltip title="Här väljer du vilka resultat som ska skickas till telefonnumret för resultatdelning.">
                  <InfoOutlinedIcon style={{ color: 'blue' }} />
                </Tooltip>
              </div>
              <select type="text" {...register('smslevel')}>
                <option value="">Missade och över promillegräns</option>
                <option value="all">Alla resultat</option>
              </select>
            </div>

            <div className={styles.threshold}>
              <label style={{ marginRight: '10px' }}>PROMILLEGRÄNS</label>
              <Tooltip title="Välj var gränsen går mellan ett godkänt och icke godkänt resultat.">
                <InfoOutlinedIcon style={{ color: 'blue' }} />
              </Tooltip>
              <select type="number" {...register('threshold')}>
                <option value="0.00">0.00</option>
                <option value="0.10">0.10</option>
                <option value="0.20">0.20</option>
              </select>
            </div>
            <div>
              <label style={{ marginRight: '10px' }}>TIDSSKILLNAD</label>
              <Tooltip title="Befinner sig klienten i en annan tidszon? Ange då här hur många timmars tidsskillnad det är mot svensk tid (CET).">
                <InfoOutlinedIcon style={{ color: 'blue' }} />
              </Tooltip>
              <select type="number" {...register('timeDiff')}>
                {timeDiffOptions.map((time, index) => (
                  <option key={index} value={time}>{`${
                    time < 0 ? time : `+${time}`
                  } h`}</option>
                ))}
              </select>
            </div>
            <div className={styles.ppmVisible}>
              <div className={styles.flex}>
                <label>VISA MÄTRESULTAT FÖR KLIENTEN?</label>
              </div>
              <select {...register('ppmVisible')}>
                <option value={false}>
                  {t('no-dont-show-ppm-after-measure')}
                </option>
                <option value={true}>{t('yes-show-ppm-after-measure')}</option>
              </select>
            </div>
          </div>
          <div>
            {saveSuccess && (
              <p style={{ color: '#2e7d32', marginBottom: '12px' }}>{saveSuccess}</p>
            )}
            {saveError && <p className="error-helper">{saveError}</p>}
            <button
              className={`${styles.btnSaveC} ${styles.btnSaveCRight}`}
              type="button"
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
            >
              {t('save-changes')}
            </button>
            <button type="button" className={styles.btnSaveC} onClick={sendSMS}>
              {t('send-new-welcome-email')}
            </button>
          </div>
        </form>
      </div>
      <Modal
        modalOpen={isOpenReactiveNotice}
        content="Kom ihåg att du behöver skriva in klientens telefonnummer på nytt"
        onConfirm={() => {
          setOpenReactiveNotice(false);
          setFocus('msisdn');
        }}
        confirmLabel={'Ok'}
        iconColor={'orange'}
      />
    </div>
  );
}

export default Patientinformation;
